// src/admin/pages/tabs/IssuancesListTab.js
import React, { useState, useEffect } from 'react';
import { FileText, Building, User, DollarSign, Calendar } from 'lucide-react';
import { supabase } from '../../../config';

const IssuancesListTab = () => {
  const [issuances, setIssuances] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [shareClasses, setShareClasses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch issuances with related data
      const { data: issuancesData, error: issuancesError } = await supabase
        .from('equity_share_issuances')
        .select(`
          *,
          company:equity_companies!equity_share_issuances_company_id_fkey(
            id,
            name,
            user_id
          ),
          shareholder:equity_shareholders!equity_share_issuances_shareholder_id_fkey(
            id,
            name,
            first_name,
            middle_name,
            last_name,
            entity_type
          ),
          share_class:equity_share_classes!equity_share_issuances_share_class_id_fkey(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (issuancesError) throw issuancesError;

      // Fetch shareholders for fallback
      const { data: shareholdersData } = await supabase
        .from('equity_shareholders')
        .select('*');

      // Fetch companies for fallback
      const { data: companiesData } = await supabase
        .from('equity_companies')
        .select('*');

      // Fetch share classes
      const { data: shareClassesData } = await supabase
        .from('equity_share_classes')
        .select('*');

      // Get unique user IDs from companies
      const userIds = [...new Set(companiesData.map(c => c.user_id).filter(Boolean))];
      
      // Fetch creator profiles
      let creators = {};
      if (userIds.length > 0) {
        const { data: creatorProfiles } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);
        
        if (creatorProfiles) {
          creators = creatorProfiles.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
      }

      // Enrich issuances with creator info
      const enrichedIssuances = (issuancesData || []).map(issuance => ({
        ...issuance,
        company: issuance.company ? {
          ...issuance.company,
          creator: creators[issuance.company.user_id] || null
        } : null
      }));

      setIssuances(enrichedIssuances);
      setShareholders(Array.isArray(shareholdersData) ? shareholdersData : []);
      setShareClasses(Array.isArray(shareClassesData) ? shareClassesData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      setIssuances([]);
      setShareholders([]);
      setShareClasses([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const getShareholderName = (issuance) => {
    if (issuance.shareholder) {
      const shareholder = issuance.shareholder;
      if (shareholder.entity_type === 'Individual') {
        return `${shareholder.first_name || ''} ${shareholder.middle_name || ''} ${shareholder.last_name || ''}`.trim();
      }
      return shareholder.name || 'Unknown Shareholder';
    }
    
    const shareholder = shareholders.find(sh => sh.id === issuance.shareholder_id);
    if (shareholder) {
      if (shareholder.entity_type === 'Individual') {
        return `${shareholder.first_name || ''} ${shareholder.middle_name || ''} ${shareholder.last_name || ''}`.trim();
      }
      return shareholder.name || 'Unknown Shareholder';
    }
    
    return 'Unknown Shareholder';
  };

  const getShareClassName = (issuance) => {
    if (issuance.share_class) {
      return issuance.share_class.name || 'Unknown Share Class';
    }
    
    const shareClass = shareClasses.find(sc => sc.id === issuance.share_class_id);
    return shareClass ? shareClass.name : 'Unknown Share Class';
  };

  const getCompanyName = (issuance) => {
    if (issuance.company && typeof issuance.company === 'object') {
      return issuance.company.name || 'Unknown Company';
    }
    const companyId = typeof issuance === 'object' ? issuance.company_id : issuance;
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const calculateTotalValue = (issuance) => {
    const shares = issuance.shares || 0;
    const price = issuance.price_per_share || 0;
    return shares * price;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading issuances...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Share Issuances</h2>
          <p className="text-gray-600 mt-1">View all share issuances across all companies</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Issuances</p>
              <p className="text-2xl font-bold text-gray-900">{issuances.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Companies</p>
              <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <User className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Shareholders</p>
              <p className="text-2xl font-bold text-gray-900">{shareholders.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">
                ${(Array.isArray(issuances) ? issuances : []).reduce((sum, i) => sum + calculateTotalValue(i), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Issuances Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shareholder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Share Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shares
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price/Share
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Round
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(issuances) && issuances.map((issuance) => (
                <tr key={issuance.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      {issuance.issue_date 
                        ? new Date(issuance.issue_date).toLocaleDateString()
                        : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Building className="h-4 w-4 text-gray-400 mr-1" />
                      {getCompanyName(issuance)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-1" />
                      {getShareholderName(issuance)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getShareClassName(issuance)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(issuance.shares || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${(issuance.price_per_share || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${calculateTotalValue(issuance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {issuance.round || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {issuance.company?.creator
                      ? ((issuance.company.creator.first_name && issuance.company.creator.last_name)
                          ? `${issuance.company.creator.first_name} ${issuance.company.creator.last_name}`
                          : issuance.company.creator.first_name || issuance.company.creator.last_name || issuance.company.creator.email || 'Unknown')
                      : 'Unknown'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {issuances.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No issuances</h3>
            <p className="mt-1 text-sm text-gray-500">No share issuances have been created yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssuancesListTab;

